import React, { useMemo } from 'react';
import { HomeContent, HomeContentProps } from 'src/components-bl/Home/Home';
import { Page, TypographyVariant } from 'src/components-dummy';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { User } from 'src/services/src/service/types/users';
import { ShopFeature } from 'src/app-types';

export type HomeContainerProps = HomeContentProps;

export function getUserDisplayName({ firstName }: Pick<User, 'firstName'>): string | undefined {
  if (firstName.length > 0) {
    return firstName;
  }

  return undefined;
}

export const HomeContainer = (props: HomeContainerProps): JSX.Element => {
  const user = useAppSelector(state => state.global.loggedInUser);
  const analyticsHomeWidget = useAppSelector(
    state => state.shop.featureToggles.analyticsHomeWidget
  );
  const experimentsHomeWidget = useAppSelector(
    state => state.shop.featureToggles.experimentsHomeWidget
  );
  const dispatch = useAppDispatch();

  const welcomeText = useMemo(() => {
    const userName = getUserDisplayName(user);

    if (!userName) {
      return 'Welcome';
    }

    return `Welcome ${userName}`;
  }, [user]);

  const isUserPermittedForAnalyticsHomeWidget = useMemo(() => {
    const { permittedRoles, enabled } = analyticsHomeWidget;

    const isPermittedForAnalyticsHomeWidget = enabled && permittedRoles.includes(user.role);
    return isPermittedForAnalyticsHomeWidget;
  }, [analyticsHomeWidget, user]);

  const isUserPermittedForExperimentsHomeWidget = useMemo(() => {
    const { permittedRoles, enabled } = experimentsHomeWidget;

    return enabled && permittedRoles.includes(user.role);
  }, [experimentsHomeWidget, user]);

  const isExperimentsFeatureEnabled =
    props.enabledShopFeatures?.has(ShopFeature.Experiments) ?? false;

  return (
    <Page layout='default' className='home-page'>
      <Page.Header>
        <Page.Title variant={TypographyVariant.SmallBold}>{welcomeText}</Page.Title>
      </Page.Header>
      <Page.Content>
        <HomeContent
          {...props}
          dispatch={dispatch}
          isUserPermittedForAnalyticsHomeWidget={isUserPermittedForAnalyticsHomeWidget}
          isExperimentsFeatureEnabled={isExperimentsFeatureEnabled}
          isUserPermittedForExperimentsHomeWidget={isUserPermittedForExperimentsHomeWidget}
          lastVisitedURL={user?.lastVisited?.uri}
        />
      </Page.Content>
    </Page>
  );
};
